import type { InboxRealtimeEvent } from './realtimeEvents';

/**
 * What the app-wide inbox socket tells everything else: each hint as it
 * arrives, and `resync` whenever hints may have been missed (a reconnect, a
 * return to the foreground, the network coming back).
 */
export type InboxSignal = InboxRealtimeEvent | { type: 'resync' };

type InboxSignalListener = (signal: InboxSignal) => void;

const listeners = new Set<InboxSignalListener>();

/**
 * Lets code outside React follow the inbox socket without opening a second
 * one. The socket lives in `useInboxLiveUpdates`, mounted once for the app; an
 * open conversation listens here to re-read itself when a hint names it.
 */
export function onInboxSignal(listener: InboxSignalListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** Called only by the inbox socket. A listener that throws does not stop the others. */
export function emitInboxSignal(signal: InboxSignal): void {
  for (const listener of [...listeners]) {
    try {
      listener(signal);
    } catch {
      // One conversation failing to refresh must not stop the rest hearing the hint.
    }
  }
}
